/* CareIQ tab fetch helpers: retry once, read the document-list meta, and
   re-read a list that the server is still refreshing.

   Used only by CareIQTab.jsx. careiqapi.js stays unchanged on purpose, because
   other screens (fax, documents, website requests) share it and some of their
   calls are writes. */
import dayjs from "dayjs";

// Statuses a v2 or BlinkScribe restart produces. Browsers usually see Apache's
// own 502/503/504 page as a network error with no response, because that page
// has no CORS headers (see api.js). So a missing response counts too.
const RETRY_STATUSES = new Set([502, 503, 504]);
export const RETRY_DELAY_MS = 2000;

// Re-read times for a list served with meta.refreshing, counted from the first
// answer (PROPOSAL step 2, H).
export const REREAD_DELAYS_MS = [15000, 45000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isCancelError = (e) =>
    !!e && (e.__CANCEL__ === true || e.name === "CanceledError" || e.code === "ERR_CANCELED");

export function isRetryable(error) {
    if (!error || isCancelError(error)) return false;
    if (!error.response) return true;            // network error (incl. CORS-less 5xx)
    return RETRY_STATUSES.has(Number(error.response.status));
}

/* Run fn(); if it fails with a retryable error, wait and run it once more.
   Only for reads. isCancelled() stops the second try after the chart moved on. */
export async function withRetry(fn, { delayMs = RETRY_DELAY_MS, isCancelled } = {}) {
    try {
        return await fn();
    } catch (e) {
        if (!isRetryable(e) || (isCancelled && isCancelled())) throw e;
        await sleep(delayMs);
        if (isCancelled && isCancelled()) throw e;
        return fn();
    }
}

/* Document-list URL for the CareIQ tab. It sends allow_stale=1, which lets the
   server answer an expired (older than 24 h) cached list at once and rebuild it
   in the background; the answer then carries refreshing: true and the tab
   re-reads it (applyListAnswer below). Only the CareIQ tab opts in. The
   Documents tab and Prior Visits build their own URLs without the flag, so for
   them an expired list still blocks and rebuilds, as before (owner, 10-05).
   A server without the flag ignores the unknown query parameter. */
export const STALE_OPT_IN_PARAM = "allow_stale";

export function docListUrl(practiceId, patientId, category) {
    return `/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/${category}?${STALE_OPT_IN_PARAM}=1`;
}

/* The documents list endpoint may add {meta: {as_of, refreshing}} next to the
   list (the API branch for PROPOSAL step 1). Older servers send no meta; that
   returns null and nothing is shown. "_list" is the name PROPOSAL.md used. */
export function readListMeta(body) {
    if (!body || typeof body !== "object" || Array.isArray(body)) return null;
    const m = body.meta || body._list || (body.data && !Array.isArray(body.data) ? body.data.meta : null);
    if (!m || typeof m !== "object") return null;
    const asOf = m.as_of ?? m.asOf ?? null;
    return { asOf: asOf === null || asOf === undefined || asOf === "" ? null : asOf, refreshing: m.refreshing === true };
}

/* Pull the array out of a list body, whatever wrap key it uses. */
export function listFrom(body, wrapKey) {
    if (Array.isArray(body)) return body;
    const d = body?.[wrapKey] ?? body?.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d[wrapKey])) return d[wrapKey];
    return [];
}

// as_of may be "YYYY-MM-DD HH:MM:SS" (local, like the cache label), ISO, a
// date, or epoch seconds/ms. dayjs reads the date-only and space forms as local.
const asOfMs = (v) => {
    if (v === null || v === undefined || v === "") return NaN;
    const d = typeof v === "number" ? dayjs(v < 1e12 ? v * 1000 : v) : dayjs(String(v));
    return d.isValid() ? d.valueOf() : NaN;
};

// A list from earlier today (visit-day refresh) shows the time: "as of Oct 5" on Oct 5 says nothing.
const fmtAsOf = (v) => {
    const t = asOfMs(v);
    if (Number.isNaN(t)) return null;
    const d = dayjs(t);
    return d.isSame(dayjs(), "day") ? d.format("h:mm A") : d.format("MMM D");
};

/* One grey provenance line for the timeline, or null.
   metas: {category: {asOf, refreshing, polling}}. Only lists the server says it
   is refreshing count. The oldest as_of is shown. "updating" stays while a
   re-read is still scheduled; after the last one the date alone remains. */
export function documentsAsOfLine(metas) {
    const live = Object.values(metas || {}).filter((m) => m && m.refreshing);
    if (live.length === 0) return null;
    const dated = live.filter((m) => !Number.isNaN(asOfMs(m.asOf)));
    const oldest = dated.sort((a, b) => asOfMs(a.asOf) - asOfMs(b.asOf))[0];
    const date = oldest ? fmtAsOf(oldest.asOf) : null;
    const updating = live.some((m) => m.polling);
    if (date) return `Documents as of ${date}${updating ? " · updating" : ""}`;
    return updating ? "Documents · updating" : null;
}

/* After a list came back with meta.refreshing, re-read it at REREAD_DELAYS_MS.
   onFresh(body, meta) runs once when as_of changes or refreshing turns false.
   onGiveUp(meta) runs if the last re-read still says refreshing (or failed).
   Returns a cancel function. */
export function scheduleRereads({ reread, asOf, onFresh, onGiveUp, delays = REREAD_DELAYS_MS }) {
    let done = false;
    const timers = [];
    const finish = () => { done = true; timers.forEach(clearTimeout); };
    delays.forEach((ms, i) => {
        const last = i === delays.length - 1;
        timers.push(setTimeout(async () => {
            if (done) return;
            let body;
            try {
                body = await reread();
            } catch (e) {
                if (last && !done) { finish(); onGiveUp && onGiveUp(null); }
                return;
            }
            if (done) return;
            const m = readListMeta(body);
            const changed = m && m.asOf !== null && String(m.asOf) !== String(asOf);
            if (!m || !m.refreshing || changed) {
                finish();
                onFresh(body, m);
            } else if (last) {
                finish();
                onGiveUp && onGiveUp(m);
            }
        }, ms));
    });
    return finish;
}

/* Apply one list answer: hand its rows to onList and its line state to onMeta
   (null when the server is not refreshing it). When it is refreshing, re-read
   it later and apply the fresh answer the same way. Returns a cancel function,
   or null when nothing was scheduled. */
export function applyListAnswer({ body, wrapKey, reread, onList, onMeta, isCancelled, delays }) {
    onList(listFrom(body, wrapKey));
    const meta = readListMeta(body);
    if (!meta || !meta.refreshing) { onMeta(null); return null; }
    onMeta({ ...meta, polling: true });
    const gone = () => !!(isCancelled && isCancelled());
    return scheduleRereads({
        reread, asOf: meta.asOf, delays,
        onFresh: (fresh, m) => {
            if (gone()) return;
            onList(listFrom(fresh, wrapKey));
            onMeta(m && m.refreshing ? { ...m, polling: false } : null);
        },
        onGiveUp: (m) => {
            if (gone()) return;
            onMeta({ ...(m || meta), refreshing: true, polling: false });
        },
    });
}
